import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';
import { AddressFormLocs } from 'libs/cm/ui/src/lib/components/cm-address-form/address-form.type';

type CPRequirementAddressFormLocs =
	| 'CPRequirementAddressFormLabelCountry'
	| 'CPRequirementAddressFormErrorCountryRequired'
	| 'CPRequirementAddressFormErrorInvalidCountry'
	| 'CPRequirementAddressFormLabelAddress'
	| 'CPRequirementAddressFormLabelCity'
	| 'CPRequirementAddressFormLabelState'
	| 'CPRequirementAddressFormLabelPostal'
	| 'CPRequirementAddressFormLabelPhone'
	| 'CPRequirementAddressFormLabelFax'
	| 'CPRequirementAddressFormErrorPhoneRequired'
	| 'CPRequirementAddressFormErrorPhoneVueUsaCanada'
	| 'CPRequirementAddressFormErrorPhoneVueNoZeros'
	| 'CPRequirementAddressFormErrorPostalRequired'
	| 'CPRequirementAddressFormErrorPostalVueUSA'
	| 'CPRequirementAddressFormErrorPostalVueCanada'
	| 'CPRequirementAddressFormErrorPostalMaxLength'
	| 'CPRequirementAddressFormErrorStateRequired'
	| 'CPRequirementAddressFormErrorStateRequiredVueHint'
	| 'CPRequirementAddressFormErrorStateInvalid'
	| 'CPRequirementAddressFormErrorCityRequired'
	| 'CPRequirementAddressFormLabelAddressContinued1'
	| 'CPRequirementAddressFormLabelAddressContinued2'
	| 'CPRequirementAddressFormLabelBusinessAddress'
	| 'CPRequirementAddressFormButtonUpdateAddress'
	| 'CPRequirementAddressFormButtonCancel'
	| 'CPRequirementAddressFormErrorAddressRequired'
	| 'CPRequirementAddressFormHintCountry'
	| 'CPRequirementAddressFormHintAddress';

const dpRoot = 'Candidate Requirement|Addresses';

const sharedLocDefs: Pick<LocDef, 'feature' | 'htmlAllowed' | 'shared' | 'portal'> = {
	feature: HubFeature.CandidateProfileAddresses,
	htmlAllowed: false,
	shared: false,
	portal: 'CP',
};

export const cpRequirementAddressFormLocs: LocDefs<CPRequirementAddressFormLocs> = {
	CPRequirementAddressFormLabelAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address',
		defaultValue: 'Address',
		description: 'Label for address.',
		displayPath: `${dpRoot}|Address`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelAddressContinued1: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address2',
		defaultValue: 'Address (cont.)',
		description: 'Label for address second line.',
		displayPath: `${dpRoot}|Address (line 2)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelAddressContinued2: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address3',
		defaultValue: 'Address (cont.)',
		description: 'Label for address third line.',
		displayPath: `${dpRoot}|Address (line 3)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelBusinessAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Business',
		defaultValue: 'This is a Business Address',
		description: 'Label for the business address checkbox.',
		displayPath: `${dpRoot}|Is Business`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelCity: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.City',
		defaultValue: 'City',
		description: 'Error saying the city is required.',
		displayPath: `${dpRoot}|City`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Country',
		defaultValue: 'Country',
		description: 'Label for country.',
		displayPath: `${dpRoot}|Country`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelFax: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Fax',
		defaultValue: 'Fax',
		description: 'Label for fax.',
		displayPath: `${dpRoot}|Fax`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelPhone: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Phone',
		defaultValue: 'Phone',
		description: 'Label for phone.',
		displayPath: `${dpRoot}|Phone`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelPostal: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.PostalCode',
		defaultValue: 'Zip/Postal Code',
		description: 'Label for zip.',
		displayPath: `${dpRoot}|Zip/Postal Code`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormLabelState: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.State',
		defaultValue: 'State/Province',
		description: 'Label for state.',
		displayPath: `${dpRoot}|State`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorAddressRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.AddressRequired',
		defaultValue: 'Address is required',
		description: 'Error saying the address is required.',
		displayPath: `${dpRoot}|Address Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorCityRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CityRequired',
		defaultValue: 'City is required',
		description: 'Error saying city is required.',
		displayPath: `${dpRoot}|City Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorCountryRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CountryRequired',
		defaultValue: 'Country is required',
		description: 'Error saying country is required.',
		displayPath: `${dpRoot}|Country Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorInvalidCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.InvalidCountry',
		defaultValue: 'Invalid country',
		description: 'Error saying the selected country is invalid.',
		displayPath: `${dpRoot}|Invalid Country`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorStateInvalid: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateInvalid',
		defaultValue: 'Invalid state/province',
		description: 'Error saying the selected state is invalid.',
		displayPath: `${dpRoot}|Invalid State`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorStateRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateRequired',
		defaultValue: 'State/Province is required',
		description: 'Error saying state is required.',
		displayPath: `${dpRoot}|State Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormErrorStateRequiredVueHint: {
		defaultValue: 'State is required. If your state is not listed, please type N/A',
		description: 'Error with Vue specific hint for state required.',
		displayPath: `${dpRoot}|State Required Vue Hint`,
		displayType: 'Error',
		htmlAllowed: false,
		shared: false,
		portal: 'CP' as LocPortal,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPostalRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalRequired',
		defaultValue: 'Zip/Postal Code is required',
		description: 'Error saying the postal code is required.',
		displayPath: `${dpRoot}|Postal Code Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPostalVueUSA: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalUsa',
		defaultValue:
			'Zip/Postal Code must follow one of the two following formats: #####, #####-####',
		description:
			'Error saying the postal code must follow one of the two following formats: #####, #####-####.',
		displayPath: `${dpRoot}|Postal Code USA Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPostalVueCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalCanada',
		defaultValue: 'Zip/Postal Code must follow the following format: A#A#A#',
		description: 'Error saying the postal code must follow the following format: a#a#a#.',
		displayPath: `${dpRoot}|Postal Code Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPhoneRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneRequired',
		defaultValue: 'Phone number is required',
		description: 'Error saying the phone number is required.',
		displayPath: `${dpRoot}|Phone Number Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPhoneVueUsaCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneUsa',
		defaultValue: 'Phone number must have 10 numbers',
		description: 'Error saying the phone number must have 10 numbers.',
		displayPath: `${dpRoot}|Phone Number USA Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPhoneVueNoZeros: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneZeros',
		defaultValue: 'Phone number must not be all zeros',
		description: 'Error saying the phone number must not be all zeros.',
		displayPath: `${dpRoot}|Phone Number No Zeros`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPRequirementAddressFormErrorPostalMaxLength: {
		defaultValue: 'Maximum number of allowed characters in postal code are: ',
		description: 'Error describing the maximum number of characters in the postal code.',
		displayPath: `${dpRoot}|Postal Code Length`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPRequirementAddressFormButtonCancel: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.Cancel',
		defaultValue: 'Cancel',
		description:
			'Button to cancel editing of candidate addresses. This is shared between mailing address, localized address and the other address fields.',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPRequirementAddressFormButtonUpdateAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.UpdateAddress',
		defaultValue: 'Update Address',
		description: 'Button to update the address.',
		displayPath: `${dpRoot}|Save`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPRequirementAddressFormHintCountry: {
		defaultValue: 'To change your country, please contact support.',
		description: 'Hint for country.',
		displayPath: `${dpRoot}|Country Hint`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileRestrictCandidateCountryUpdates,
		htmlAllowed: true,
		shared: false,
		portal: 'CP' as LocPortal,
	},
	CPRequirementAddressFormHintAddress: {
		defaultValue: '',
		description: 'Hint for address.',
		displayPath: `${dpRoot}|Address Hint`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileAddresses,
		htmlAllowed: false,
		shared: false,
		portal: 'CP' as LocPortal,
	},
};

export const REQUIREMENT_ADDRESS_FORM_LOC_KEYS = MapLocKeys<CPRequirementAddressFormLocs>(
	cpRequirementAddressFormLocs
);
export type CpRequirementAddressFormLocKeys = LocMap<CPRequirementAddressFormLocs>;

export const mappedAddressFormLocs: AddressFormLocs = {
	AddressFormLabelCountry: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelCountry,
	AddressFormLabelFax: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelFax,
	AddressFormErrorCountryRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorCountryRequired,
	AddressFormErrorInvalidCountry:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorInvalidCountry,
	AddressFormLabelAddress: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelAddress,
	AddressFormLabelCity: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelCity,
	AddressFormLabelState: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelState,
	AddressFormLabelPostal: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelPostal,
	AddressFormLabelPhone: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelPhone,
	AddressFormErrorPhoneRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPhoneRequired,
	AddressFormErrorPhoneVueUsaCanada:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPhoneVueUsaCanada,
	AddressFormErrorPhoneVueNoZeros:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPhoneVueNoZeros,
	AddressFormErrorPostalRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPostalRequired,
	AddressFormErrorPostalVueUSA:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPostalVueUSA,
	AddressFormErrorPostalVueCanada:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPostalVueCanada,
	AddressFormErrorPostalMaxLength:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorPostalMaxLength,
	AddressFormErrorStateRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorStateRequired,
	AddressFormErrorStateRequiredVueHint:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorStateRequiredVueHint,
	AddressFormErrorStateInvalid:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorStateInvalid,
	AddressFormErrorCityRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorCityRequired,
	AddressFormLabelAddressContinued2:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelAddressContinued1,
	AddressFormLabelAddressContinued3:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelAddressContinued2,
	AddressFormLabelBusinessAddress:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormLabelBusinessAddress,
	AddressFormButtonUpdateAddress:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormButtonUpdateAddress,
	AddressFormButtonCancel: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormButtonCancel,
	AddressFormErrorAddressRequired:
		REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormErrorAddressRequired,
	AddressFormHintCountry: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormHintCountry,
	AddressFormHintAddress: REQUIREMENT_ADDRESS_FORM_LOC_KEYS.CPRequirementAddressFormHintAddress,
};
